import React, { useEffect, useRef, useState } from "react";
/* Contexts */
import useDeceased from "../../hooks/useDeceased";
/* Helpers */
import { toggleDetailsView } from "../../helper/toggleMenuView";
/* Resources */
import useAuth from "../../hooks/useAuth";
import useData from "../../hooks/useData";
import { getAgencyDefaultDeceasedImage } from "../../api/pro/agencyReq";
import { safeJwtDecode } from "../../helper/jwtHelper";
import { deleteDeceasedFromDb, setDeceasedEnabled } from "../../api/pro/deceasedReq";
import { getErrorListFromValidation, removeErrorList, capitalize } from "../../helper/tools";
import { Link } from "react-router-dom";


export default function DeceasedDetails({ location, admin, deceasedListApi }) {
    const { deceasedInfos, deceased } = useDeceased();
    const { auth } = useAuth();
    const token = auth.token;
    const decodedToken = safeJwtDecode(token);
    const agencyId = decodedToken?.agency;
    const IMG_FOLDER = process.env.REACT_APP_LOCAL_ASSETS_BASE_URL;
    const { agencyDefaultDeceasedImage, setAgencyDefaultDeceasedImage } = useData();

    const [showThreeButtons, setShowThreeButtons] = useState(typeof window !== 'undefined' ? window.innerWidth <= 450 : false);

    useEffect(() => {
        const onResize = () => setShowThreeButtons(window.innerWidth <= 450);
        window.addEventListener('resize', onResize);
        onResize();
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const apiDefaultImage = async () => {
        const response = await getAgencyDefaultDeceasedImage(token, agencyId)
        // Gérer le cas où l'API retourne un tableau ou un objet
        let data;
        if (Array.isArray(response)) {
            data = response.length > 0 ? response[0] : {};
        } else {
            data = response || {};
        }

        // Normaliser la propriété defaultImage si elle a un nom différent
        if (data && !data.defaultImage) {
            if (data.image) {
                data = { ...data, defaultImage: data.image };
            } else if (data.default_image) {
                data = { ...data, defaultImage: data.default_image };
            }
        }

        setAgencyDefaultDeceasedImage(data);
    }

    const handleChangeDeceasedEnabled = async (e) => {
        e.preventDefault();
        if (!auth.roles.includes("ROLE_PF" && "ROLE_UPF" && "ROLE_PREVIOUS_ADMIN")) {
            alert("Vous n'avez pas les droits pour modifier la visibilité d'un défunt.");
        } else {
            const deceasedId = parseInt(e.currentTarget.getAttribute("id"));
            const currentIsListed = e.currentTarget.classList.contains("fa-eye");

            if (currentIsListed) {
                let body = {
                    "deceasedEnabled": true,
                    "deceasedId": deceasedId,
                }
                alert("Vous venez de rendre invisible ce défunt.");
                await setDeceasedEnabled(token, body)
                    .then((response) => {
                        removeErrorList();
                        if (!response?.response?.data?.error) {
                            deceasedListApi()
                        } else {
                            getErrorListFromValidation(response?.response?.data?.error)
                        }
                    })
            } else {
                let body = {
                    "deceasedEnabled": false,
                    "deceasedId": deceasedId,
                }
                alert("Vous venez de rendre visible ce défunt.");
                await setDeceasedEnabled(token, body)
                    .then((response) => {
                        removeErrorList();
                        if (!response?.response?.data?.error) {
                            deceasedListApi()
                        } else {
                            getErrorListFromValidation(response?.response?.data?.error)
                        }
                    })
            }
        }
    }

    const handleDeleteDeceased = async (e) => {
        e.preventDefault();
        const id = e.currentTarget.getAttribute("id");
        if (!auth.roles.includes("ROLE_PREVIOUS_ADMIN")) {
            alert("Vous n'avez pas les droits pour supprimer définitivement un défunt.");
        } else {
            if (window.confirm("Êtes-vous sûr de vouloir supprimer définitivement ce défunt ?")) {
                const body = {
                    "deceasedId": parseInt(id),
                }
                await deleteDeceasedFromDb(token, body)
                deceasedListApi()
            }
        }
    }

    useEffect(() => {
        apiDefaultImage()
    }, [agencyId, token]);


    return (
        <div id="0" className="active details deceased-resume">
            <div className="arrow" onClick={toggleDetailsView}>
                <span><i className="fa fa-chevron-left"></i></span>
            </div>
            <legend>
                <i className="far fa-user" />
                <span>Informations du défunt</span>
            </legend>
            <div className="details-content">
                {deceasedInfos ?
                    (location === "Tableau de bord") &&
                        <>
                            <div className="avatar">
                                <img src={
                                    // Si pas de portrait défini, afficher l'image par défaut.
                                    // isAgencySpecific (calculé côté back via file_exists) indique le
                                    // dossier réel : sans lui, une image propre à l'agence (absente du
                                    // dossier racine partagé) retombait sur le onError générique ci-dessous.
                                    !deceasedInfos?.portrait || deceasedInfos?.portrait === "" ?
                                        (agencyDefaultDeceasedImage?.isAgencySpecific
                                            ? `${IMG_FOLDER}/img/deceased_library/${agencyId}/${agencyDefaultDeceasedImage?.defaultImage}`
                                            : `${IMG_FOLDER}/img/deceased_library/${agencyDefaultDeceasedImage?.defaultImage}`)
                                    // Si portrait de la bibliothèque
                                    : deceasedInfos?.useGalleryPortrait ?
                                        `${IMG_FOLDER}/img/deceased_library/${agencyId}/${deceasedInfos?.portrait}`
                                    // Si portrait uploadé personnalisé
                                    : deceasedInfos?.uploadDir ?
                                        `${IMG_FOLDER}/img/${deceasedInfos?.uploadDir}/${deceasedInfos?.portrait}`
                                    // Fallback: essayer la bibliothèque sans agencyId
                                    : `${IMG_FOLDER}/img/deceased_library/${deceasedInfos?.portrait}`
                                }
                                    className='portrait'
                                    alt="portrait du défunt"
                                    onError={(e) => {
                                        // En cas d'erreur avec le chemin agency, essayer root
                                        if (e.target.src.includes(`/${agencyId}/`)) {
                                            e.target.onerror = null;
                                            const filename = deceasedInfos?.portrait || agencyDefaultDeceasedImage?.defaultImage;
                                            e.target.src = `${IMG_FOLDER}/img/deceased_library/${filename}`;
                                        } else if (!e.target.src.includes('default.jpg')) {
                                            e.target.onerror = null;
                                            e.target.src = `${IMG_FOLDER}/img/deceased_library/default.jpg`;
                                        }
                                    }} />
                            </div>
                            <div className="deceased-infos">
                                <div className="info-line">
                                    <div className="field">
                                        <p>Défunt:</p>
                                        <span>{!deceasedInfos?.hiddenCivility && (deceasedInfos?.civility === "MME" ? "Madame " : "Monsieur ")}{capitalize(deceasedInfos?.firstName)} </span>
                                        <span>{deceasedInfos?.lastName?.toUpperCase()}</span>
                                    </div>
                                    {deceasedInfos?.birthDate &&
                                        <div className="field">
                                            <p>Date de naissance:</p>
                                            <span>
                                                {new Date(deceasedInfos.birthDate).toLocaleDateString('fr-FR')}
                                                {deceasedInfos?.birthplace?.city && ` à ${deceasedInfos.birthplace.city}`}
                                                {deceasedInfos?.birthplace?.zipcode && ` (${deceasedInfos.birthplace.zipcode})`}
                                            </span>
                                        </div>
                                    }
                                    {deceasedInfos?.actualplace?.address &&
                                        <div className="field">
                                            <p>Adresse:</p>
                                            <span>
                                                {deceasedInfos.actualplace.address}
                                                {deceasedInfos?.actualplace?.city && ` - ${deceasedInfos.actualplace.city}`}
                                                {deceasedInfos?.actualplace?.zipcode && ` - ${deceasedInfos.actualplace.zipcode}`}
                                            </span>
                                        </div>
                                    }
                                    {deceasedInfos?.deathDate &&
                                        <div className="field">
                                            <p>Date de décès:</p>
                                            <span>
                                                {new Date(deceasedInfos.deathDate).toLocaleDateString('fr-FR')}
                                                {deceasedInfos?.deathplace?.city && ` à ${deceasedInfos.deathplace.city}`}
                                                {deceasedInfos?.deathplace?.zipcode && ` (${deceasedInfos.deathplace.zipcode})`}
                                            </span>
                                        </div>
                                    }
                                </div>
                                {(admin?.firstName || admin?.lastName) &&
                                    <div className="info-line">
                                        <p>Gestionnaire:</p>
                                        <span>{admin?.civility === "MME" ? "Madame" : "Monsieur"} {capitalize(admin?.firstName)} </span>
                                        <span>{admin?.lastName?.toUpperCase()}</span>
                                        {admin?.phone &&
                                            <div className="phone-line">
                                                <i className="fa-solid fa-phone" /><span> {admin?.phone}</span>
                                            </div>
                                        }
                                    </div>
                                }
                                {deceasedInfos?.ceremonyInfos &&
                                    <div className="info-line">
                                        <p>Cérémonie:</p>
                                        <span>{deceasedInfos?.ceremonyInfos?.placeLabel} {deceasedInfos?.ceremonyInfos?.placeName}</span><br />
                                        <span>le {deceasedInfos?.ceremonyInfos?.date ? new Date(deceasedInfos.ceremonyInfos.date).toLocaleDateString('fr-FR') : deceasedInfos?.ceremonyInfos?.date} à {deceasedInfos?.ceremonyInfos?.time}</span>
                                    </div>
                                }
                            </div>
                            {/*{showThreeButtons &&
                                <div className="three-buttons">
                                    <Link to={`/defunt/${deceasedInfos?.id}`}><button className="btn blue-button">
                                        <i className="fa fa-gear"></i>
                                    </button>
                                    </Link>
                                    <button id={deceasedInfos?.id}
                                        className="btn red-button" onClick={handleDeleteDeceased}>
                                        <i className="fa fa-trash"></i>
                                    </button>
                                    <button className="btn green-button">
                                        <i
                                            id={deceasedInfos?.id}
                                            className={(deceasedInfos?.isListed === 1 || deceasedInfos?.isListed === "1" || deceasedInfos?.isListed === true) ? "fa fa-eye" : "fa fa-eye-slash"}
                                            onClick={(e) => handleChangeDeceasedEnabled(e)}
                                            title={(deceasedInfos?.isListed === 1 || deceasedInfos?.isListed === "1" || deceasedInfos?.isListed === true) ? "Défunt visible - Cliquer pour masquer" : "Défunt masqué - Cliquer pour rendre visible"}
                                        />
                                    </button>
                                </div>
                            }*/}
                        </>
                    : <p className="default-message">Sélectionnez un défunt</p>
                }
            </div>
        </div>
    )
}
